import { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, InputAdornment, Paper, Popper, TextField, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import UserAvatar from "../UserAvatar";
import UserPlaylistsDialog from "../UserPlaylistsDialog";
import { searchUsers, UserSearchResult } from "../../../services/user";
import { colors } from "../../../theme";

const DEBOUNCE_MS = 250;

export default function SearchBar() {
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState<UserSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const query = searchText.trim();
        if (!query) {
            setResults([]);
            setLoading(false);
            return;
        }

        // Debounced so typing a name doesn't fire a request per keystroke. `cancelled`
        // drops responses that come back after a newer query has already been typed -
        // otherwise a slow early request can overwrite the newer results.
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const found = await searchUsers(query);
                if (!cancelled) {
                    setResults(found);
                }
            } catch {
                if (!cancelled) {
                    setResults([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchText]);

    const showDropdown = open && searchText.trim().length > 0;

    return (
        <>
            <Box ref={anchorRef}>
                <TextField
                    id="shufl-search"
                    type="search"
                    placeholder="Search people"
                    value={searchText}
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoComplete="off"
                    onChange={(event) => { setSearchText(event.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    // Deferred so a click on a result registers before the list unmounts.
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: colors.elevatedHover,
                            borderRadius: 500,
                            color: colors.textPrimary,
                            fontSize: 14,
                            "& fieldset": { borderColor: "transparent" },
                            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                            "&.Mui-focused fieldset": { borderColor: colors.accent, borderWidth: 2 },
                        },
                        "& input::placeholder": { color: colors.textMuted, opacity: 1 },
                    }}
                />
            </Box>

            <Popper
                open={showDropdown}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
            >
                <Paper
                    sx={{
                        mt: 0.75,
                        py: 0.5,
                        borderRadius: 2,
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        maxHeight: 340,
                        overflowY: "auto",
                    }}
                >
                    {loading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}

                    {!loading && results.length === 0 && (
                        <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: colors.textSecondary }}>
                            No one found matching "{searchText.trim()}"
                        </Typography>
                    )}

                    {results.map(user => (
                        <ButtonBase
                            key={user.id}
                            onClick={() => { setSelectedUser(user); setOpen(false); }}
                            sx={{
                                width: "100%",
                                justifyContent: "flex-start",
                                gap: 1.25,
                                px: 2,
                                py: 1,
                                textAlign: "left",
                                "&:hover": { backgroundColor: colors.elevatedHover },
                            }}
                        >
                            <UserAvatar username={user.username} imageUrl={user.profilePictureUrl} size={32} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                                    {user.username}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                                    {user.playlistCount} playlist{user.playlistCount === 1 ? "" : "s"}
                                </Typography>
                            </Box>
                        </ButtonBase>
                    ))}
                </Paper>
            </Popper>

            <UserPlaylistsDialog user={selectedUser} onClose={() => setSelectedUser(null)} />
        </>
    );
}
